import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';

interface WorkOrderReasonDialogProps {
  open: boolean;
  type: 'dispute' | 'invoicing_hold' | 'cancel' | 'reopen';
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  submitting: boolean;
  /** Cancel-only (#2914): keep the work order and open the existing schedule editor. */
  onReschedule?: () => void;
}

export function WorkOrderReasonDialog({
  open,
  type,
  value,
  onChange,
  onClose,
  onSubmit,
  submitting,
  onReschedule,
}: WorkOrderReasonDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');

  const title =
    type === 'dispute'
      ? t('workOrderActions.dispute')
      : type === 'invoicing_hold'
        ? t('workOrderActions.invoicingHold')
        : type === 'reopen'
          ? t('serviceActions.reopenService')
          : t('workOrderActions.cancelWorkOrder');
  // Reopening a service only needs a reason when someone wants to give one (#3403).
  const reasonOptional = type === 'reopen';

  const isCancelChoice = type === 'cancel';

  return (
    // ResponsiveDialog, not a raw Dialog: on a tablet this goes full
    // screen with its own close button instead of a cramped centred modal
    // (#2722).
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth mobileTitle={title}>
      <form onSubmit={onSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {isCancelChoice ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('workOrderActions.cancelOrRescheduleHint')}
            </Typography>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={value}
            onChange={(event) => { onChange(event.target.value); }}
            label={
              type === 'dispute'
                ? t('workOrderActions.disputeReason')
                : type === 'invoicing_hold'
                  ? t('workOrderActions.invoicingHoldReason')
                  : type === 'reopen'
                    ? t('serviceActions.reopenReason')
                    : t('workOrderActions.cancelReason')
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            {tc('cancel')}
          </Button>
          {isCancelChoice ? (
            <>
              <Button
                type="button"
                variant="outlined"
                onClick={() => { onReschedule?.(); }}
                disabled={submitting || !onReschedule}
              >
                {t('workOrderActions.reschedule')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="error"
                /*
                  Every reason is required, cancel included (#2722).

                  The guard used to read `type !== 'cancel' && !value.trim()`,
                  which is backwards with respect to severity: dispute and
                  invoicing hold are both reversible and demanded a reason, while
                  cancel — the one that ends the work order — was the single case
                  let through blank. `bookings.cancellation_reason` is nullable
                  and the route only writes it `if (reason)`, so a blank
                  cancellation left no record of why.
                */
                disabled={!value.trim() || submitting}
              >
                {t('workOrderActions.cancelCompletely')}
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={(!reasonOptional && !value.trim()) || submitting}
            >
              {tc('confirm')}
            </Button>
          )}
        </DialogActions>
      </form>
    </ResponsiveDialog>
  );
}
